import React from "react"
import {
    Avatar,
    Box,
    Card,
    CardContent,
    Chip,
    Divider,
    Link,
    Stack,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} from "@mui/material"
import RedditIcon from "@mui/icons-material/Reddit"
import PublicIcon from "@mui/icons-material/Public"
import WorkOutlineIcon from "@mui/icons-material/WorkOutline"
import PersonOutlineIcon from "@mui/icons-material/PersonOutline"
import LinkIcon from "@mui/icons-material/Link"
import FactCheckIcon from "@mui/icons-material/FactCheck"
import GitHubIcon from "@mui/icons-material/GitHub"
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api"

type Props = {
    redditResults: any[]
}

export default function RedditResultsViewer({ redditResults }: Props) {
    const [currentTab, setCurrentTab] = React.useState(0)
    const result = redditResults?.[0]
    const report = result?.report

    if (!report) {
        return null
    }

    const profile = report.profile_assessment
    const location =
        profile?.possible_location?.city ||
        profile?.possible_location?.region ||
        profile?.possible_location?.country

    const [locationCoords, setLocationCoords] = React.useState<{
        lat: number
        lng: number
    } | null>(null)

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: process.env.REACT_APP_FIREBASE_API_KEY || "",
    })

    React.useEffect(() => {
        if (!location) {
            setLocationCoords(null)
            return
        }

        let cancelled = false

        async function loadLocation() {
            try {
                const response = await fetch(
                    `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&q=${encodeURIComponent(location)}`,
                    {
                        headers: {
                            Accept: "application/json",
                            "Accept-Language": "en",
                        },
                    },
                )

                const json = await response.json()
                console.log("Nominatim json:", json)

                const first = Array.isArray(json) ? json[0] : null
                const lat = Number(first?.lat)
                const lng = Number(first?.lon)

                if (!cancelled && Number.isFinite(lat) && Number.isFinite(lng)) {
                    setLocationCoords({ lat, lng })
                } else if (!cancelled) {
                    setLocationCoords(null)
                }
            } catch (e) {
                console.error("Nominatim failed:", e)

                if (!cancelled) {
                    setLocationCoords(null)
                }
            }
        }

        loadLocation()

        return () => {
            cancelled = true
        }
    }, [location])


    const summary = report.collection_summary
    const evidence = report.evidence ?? []
    const crossPlatform = profile?.cross_platform_matches ?? []

    const handleChangeTab = (_event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue)
    }

    const formatNumber = (value?: number | null) => {
        if (value === null || value === undefined) return "—"
        return value.toLocaleString()
    }

    const formatDate = (value?: string) => {
        if (!value) return "—"
        return new Date(value).toLocaleString()
    }

    const getConfidenceColor = (confidence?: string) => {
        switch (confidence?.toLowerCase()) {
            case "very high":
                return "success"
            case "high":
                return "success"
            case "medium":
                return "warning"
            case "low":
                return "default"
            default:
                return "default"
        }
    }

    const renderValue = (value?: any) => {
        if (value === null || value === undefined || value === "") return "—"
        return String(value)
    }

    const possibleProducts =
        profile?.professional_indicators?.possible_products
            ?.map((item: any) => {
                try {
                    return typeof item === "string" ? JSON.parse(item) : item
                } catch {
                    return null
                }
            })
            .filter(Boolean) ?? []

    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: 3,
                overflow: "hidden",
            }}
        >
            <CardContent sx={{ p: 0 }}>
                <Tabs
                    value={currentTab}
                    onChange={handleChangeTab}
                    aria-label="reddit result tabs"
                    sx={{
                        px: 3,
                        borderBottom: 1,
                        borderColor: "divider",
                    }}
                >
                    <Tab label="Profile" />
                    <Tab label={`Evidence (${evidence.length})`} />

                    {crossPlatform.length > 0 && (
                        <Tab label={`Cross platform (${crossPlatform.length})`} />
                    )}

                    <Tab label="Summary" />
                </Tabs>

                <Box role="tabpanel" hidden={currentTab !== 0}>
                    {currentTab === 0 && (
                        <Box sx={{ p: 3 }}>
                            <Stack spacing={3}>
                                <Stack direction="row" spacing={2} alignItems="flex-start">
                                    <Avatar
                                        sx={{
                                            width: 76,
                                            height: 76,
                                            bgcolor: "error.main",
                                        }}
                                    >
                                        <RedditIcon fontSize="large" />
                                    </Avatar>

                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="h6" fontWeight={700} noWrap>
                                            {report.username}
                                        </Typography>

                                        <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap">
                                            <Chip
                                                size="small"
                                                label={report.status}
                                                color={report.status === "completed" ? "success" : "default"}
                                            />

                                            <Chip
                                                size="small"
                                                label={`Depth: ${summary?.analysis_depth ?? "—"}`}
                                                variant="filled"
                                                 sx={{color: "#000" }}
                                                
                                            />

                                            <Chip
                                                size="small"
                                                label={`Status: ${summary?.collection_status ?? "—"}`}
                                                variant="filled"
                                                sx={{color: "#000" }}
                                            />
                                        </Stack>
                                    </Box>
                                </Stack>
                                <Divider />
                                {isLoaded && locationCoords && (
                                    <Section title="Location" icon={<PublicIcon />}>
                                        <Box
                                            sx={{
                                                overflow: "hidden",
                                                borderRadius: 2,
                                                border: "1px solid",
                                                borderColor: "divider",
                                            }}
                                        >
                                            <GoogleMap
                                                mapContainerStyle={{
                                                    width: "100%",
                                                    height: "260px",
                                                }}
                                                center={locationCoords}
                                                zoom={location === profile?.possible_location?.country ? 5 : 9}
                                                options={{
                                                    streetViewControl: false,
                                                    mapTypeControl: false,
                                                    fullscreenControl: false,
                                                }}
                                            >
                                                <Marker position={locationCoords} />
                                            </GoogleMap>
                                        </Box>

                                        <Link
                                            href={`https://www.google.com/maps?q=${locationCoords.lat},${locationCoords.lng}`}
                                            target="_blank"
                                            underline="hover"
                                            sx={{ mt: 1, display: "inline-block" }}
                                        >
                                            Open in Google Maps →
                                        </Link>
                                    </Section>
                                )}
                                <Stack direction="row" spacing={1.5} flexWrap="wrap">
                                    <InfoChip
                                        icon={<PublicIcon fontSize="small" />}
                                        label="Country"
                                        value={profile?.possible_location?.country}
                                    />
                                    <InfoChip
                                        icon={<PersonOutlineIcon fontSize="small" />}
                                        label="Gender"
                                        value={profile?.gender_indicators?.value}
                                    />

                                    <InfoChip
                                        icon={<PersonOutlineIcon fontSize="small" />}
                                        label="Age"
                                        value={profile?.age_range?.value}
                                    />

                                    <InfoChip
                                        icon={<FactCheckIcon fontSize="small" />}
                                        label="Confidence"
                                        value={profile?.professional_indicators?.confidence}
                                    />
                                </Stack>

                                <Section title="Professional indicators" icon={<WorkOutlineIcon />}>
                                    <Stack spacing={2}>
                                        {!!profile?.professional_indicators?.possible_roles?.length && (
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight={700} mb={1}>
                                                    Possible roles
                                                </Typography>

                                                <Stack
                                                    direction="row"
                                                    flexWrap="wrap"
                                                    sx={{
                                                        gap: 1,
                                                    }}
                                                >
                                                    {profile.professional_indicators.possible_roles.map(
                                                        (role: string) => (
                                                            <Chip key={role} label={role} size="small" sx={{color: "#000" }}/>
                                                        ),
                                                    )}
                                                </Stack>
                                            </Box>
                                        )}

                                        {!!profile?.professional_indicators?.role_self_claims?.length && (
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight={700} mb={1}>
                                                    Self claims
                                                </Typography>

                                                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                                                    {profile.professional_indicators.role_self_claims.map(
                                                        (claim: string) => (
                                                            <Chip
                                                                key={claim}
                                                                label={claim}
                                                                size="small"
                                                                variant="filled"
                                                                sx={{color: "#000" }}
                                                            />
                                                        ),
                                                    )}
                                                </Stack>
                                            </Box>
                                        )}

                                        {!!possibleProducts.length && (
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight={700} mb={1}>
                                                    Products
                                                </Typography>

                                                <Stack spacing={1.5}>
                                                    {possibleProducts.map((product: any, index: number) => (
                                                        <Card key={index} variant="outlined" sx={{ borderRadius: 2 }}>
                                                            <CardContent sx={{
                                                                p: 3,
                                                                "&:last-child": {
                                                                    pb: 3,
                                                                },
                                                            }}>
                                                                <Stack spacing={1}>
                                                                    <Stack
                                                                        direction="row"
                                                                        spacing={1}
                                                                        alignItems="center"
                                                                        flexWrap="wrap"
                                                                    >
                                                                        <Typography fontWeight={700}>
                                                                            {product.product_name}
                                                                        </Typography>
                                                                        <Tooltip
                                                                            arrow
                                                                            title="Confidence impact indicates how strongly this piece of evidence supports the inferred attribute."
                                                                        >
                                                                            <Chip
                                                                                size="small"
                                                                                label={product.confidence ?? "unknown"}
                                                                                color={getConfidenceColor(product.confidence) as any}
                                                                                variant="filled"
                                                                                 sx={{color: "#000" }}
                                                                            />
                                                                        </Tooltip>
                                                                    </Stack>

                                                                    {!!product.evidence?.[0]?.quote_or_excerpt && (
                                                                        <Typography
                                                                            variant="body2"
                                                                            color="text.secondary"
                                                                            sx={{ lineHeight: 1.7 }}
                                                                        >
                                                                            “{product.evidence[0].quote_or_excerpt}”
                                                                        </Typography>
                                                                    )}

                                                                    {!!product.evidence?.[0]?.source_url && (
                                                                        <Link
                                                                            href={product.evidence[0].source_url}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            underline="hover"
                                                                            sx={{ fontSize: "0.875rem" }}
                                                                        >
                                                                            View source →
                                                                        </Link>
                                                                    )}
                                                                </Stack>
                                                            </CardContent>
                                                        </Card>
                                                    ))}
                                                </Stack>
                                            </Box>
                                        )}
                                    </Stack>
                                </Section>

                                {!!profile?.communication_profile && (
                                    <Section title="Communication profile" icon={<PersonOutlineIcon />}>
                                        <Stack spacing={2}>
                                            <Typography sx={{ lineHeight: 1.7 }}>
                                                <Box component="span" fontWeight={700}>
                                                    Tone:
                                                </Box>{" "}
                                                {renderValue(profile.communication_profile.tone)}
                                            </Typography>

                                            <Typography sx={{ lineHeight: 1.7 }}>
                                                <Box component="span" fontWeight={700}>
                                                    Style:
                                                </Box>{" "}
                                                {renderValue(profile.communication_profile.style)}
                                            </Typography>

                                            {!!profile.communication_profile.main_topics?.length && (
                                                <Stack
                                                    direction="row"
                                                    flexWrap="wrap"
                                                    sx={{
                                                        gap: 1,
                                                    }}
                                                >
                                                    {profile.communication_profile.main_topics.map(
                                                        (topic: string) => (
                                                            <Chip key={topic} label={topic} size="small" sx={{color: "#000" }} />
                                                        ),
                                                    )}
                                                </Stack>
                                            )}
                                        </Stack>
                                    </Section>
                                )}

                                {!!profile?.linked_usernames?.length && (
                                    <Section title="Linked usernames" icon={<LinkIcon />}>
                                        <Stack direction="row" spacing={1} flexWrap="wrap">
                                            {profile.linked_usernames.map((username: string) => (
                                                <Chip key={username} label={username} size="small" sx={{color: "#000" }}/>
                                            ))}
                                        </Stack>
                                    </Section>
                                )}
                            </Stack>
                        </Box>
                    )}
                </Box>

                <Box role="tabpanel" hidden={currentTab !== 1}>
                    {currentTab === 1 && (
                        <Box sx={{ p: 3 }}>
                            <Stack spacing={2}>
                                {evidence.map((item: any, index: number) => (
                                    <Card
                                        key={`${item.source_url}-${index}`}
                                        variant="outlined"
                                        sx={{
                                            borderRadius: 3,
                                            transition: "all .2s ease",
                                            "&:hover": {
                                                boxShadow: 2,
                                            },
                                        }}
                                    >
                                        <CardContent
                                            sx={{
                                                p: 3,
                                                "&:last-child": {
                                                    pb: 3,
                                                },
                                            }}
                                        >
                                            <Stack spacing={2}>
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                    flexWrap="wrap"
                                                >
                                                    <Chip
                                                        size="small"
                                                        sx={{color: "#000" }}
                                                        label={item.source_type ?? "source"}
                                                        color={item.source_type === "reddit" ? "error" : "default"}
                                                        icon={
                                                            item.source_type === "reddit" ? (
                                                                <RedditIcon />
                                                            ) : (
                                                                <PublicIcon />
                                                            )
                                                        }
                                                    />

                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Tooltip
                                                            arrow
                                                            title="Confidence impact indicates how strongly this piece of evidence supports the inferred attribute."
                                                        >
                                                            <Chip
                                                                size="small"
                                                                label={item.confidence_impact ?? "unknown"}
                                                                color={getConfidenceColor(item.confidence_impact) as any}
                                                                variant="filled"
                                                                sx={{color: "#000" }}
                                                            />
                                                        </Tooltip>


                                                    </Stack>

                                                </Stack>

                                                {!!item.quote_or_excerpt && (
                                                    <Typography
                                                        sx={{
                                                            lineHeight: 1.8,
                                                            textAlign: "left",
                                                            fontSize: "0.95rem",
                                                        }}
                                                    >
                                                        “{item.quote_or_excerpt}”
                                                    </Typography>
                                                )}

                                                <Stack spacing={0.5}>
                                                    {item.inference_type && (
                                                        <Typography variant="body2" color="text.secondary">
                                                            Inference: {item.inference_type}
                                                        </Typography>
                                                    )}

                                                    {item.created_utc !== null &&
                                                        item.created_utc !== undefined &&
                                                        item.created_utc !== 0 && (
                                                            <Typography variant="body2" color="text.secondary">
                                                                Created UTC: {formatUtcDate(item.created_utc)}
                                                            </Typography>
                                                        )}
                                                </Stack>

                                                {!!item.source_url && item.source_url !== 'N/A' && (
                                                    <Link
                                                        href={item.source_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        underline="hover"
                                                        sx={{
                                                            fontWeight: 500,
                                                            wordBreak: "break-all",
                                                        }}
                                                    >
                                                        Open source →
                                                    </Link>
                                                )}
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Stack>
                        </Box>
                    )}
                </Box>

                {crossPlatform.length > 0 && (
                    <Box role="tabpanel" hidden={currentTab !== 2}>
                        {currentTab === 2 && (
                            <Box sx={{ p: 3 }}>
                                <Stack spacing={2}>
                                    {crossPlatform.map((item: any, index: number) => (
                                        <Card key={`${item.platform}-${index}`} variant="outlined" sx={{ borderRadius: 3 }}>
                                            <CardContent
                                                sx={{
                                                    p: 3,
                                                    "&:last-child": {
                                                        pb: 3,
                                                    },
                                                }}
                                            >
                                                <Stack spacing={2}>
                                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                                        <Avatar sx={{ bgcolor: "action.hover", color: "text.primary" }}>
                                                            {item.platform?.toLowerCase() === "github" ? (
                                                                <GitHubIcon />
                                                            ) : (
                                                                <LinkIcon />
                                                            )}
                                                        </Avatar>

                                                        <Box>
                                                            <Stack spacing={0.5}>
                                                                <Typography fontWeight={700}>
                                                                    {item.platform}
                                                                </Typography>

                                                                <Typography variant="body2" color="text.secondary">
                                                                    {item.username}
                                                                </Typography>
                                                            </Stack>
                                                        </Box>
                                                    </Stack>

                                                    <Stack direction="row" spacing={1} flexWrap="wrap">
                                                        <Chip
                                                            size="small"
                                                            label={`Confidence: ${item.confidence_label ?? "unknown"}`}
                                                            color={getConfidenceColor(item.confidence_label) as any}
                                                            variant="filled"
                                                            sx={{color: "#000" }}
                                                        />

                                                        <Chip
                                                            size="small"
                                                            label={`Score: ${item.match_confidence_score ?? "—"}`}
                                                            variant="filled"
                                                            sx={{color: "#000" }}
                                                        />
                                                    </Stack>

                                                    {!!item.assessment && (
                                                        <Typography sx={{ lineHeight: 1.7 }}>
                                                            {item.assessment}
                                                        </Typography>
                                                    )}

                                                    {!!item.url && (
                                                        <Link
                                                            href={item.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            underline="hover"
                                                            sx={{ fontWeight: 500, wordBreak: "break-all" }}
                                                        >
                                                            Open profile →
                                                        </Link>
                                                    )}
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </Box>
                )}

                <Box role="tabpanel" hidden={currentTab !== (crossPlatform.length > 0 ? 3 : 2)}>
                    {currentTab === (crossPlatform.length > 0 ? 3 : 2) && (
                        <Box sx={{ p: 3 }}>
                            <Stack spacing={3}>
                                <Section title="Collection summary" icon={<FactCheckIcon />}>
                                    <Stack direction="row" spacing={1.5} flexWrap="wrap">
                                        <InfoChip
                                            label="Profile"
                                            value={summary?.reddit_profile_collected ? "Collected" : "Not collected"}
                                        />

                                        <InfoChip
                                            label="Posts"
                                            value={formatNumber(summary?.reddit_posts_count)}
                                        />

                                        <InfoChip
                                            label="Comments"
                                            value={formatNumber(summary?.reddit_comments_count)}
                                        />

                                        <InfoChip
                                            label="External mentions"
                                            value={formatNumber(summary?.external_mentions_count)}
                                        />
                                    </Stack>
                                </Section>

                                <Section title="Request info" icon={<RedditIcon />}>
                                    <Stack spacing={1}>
                                        <Typography variant="body2">
                                            <Box component="span" fontWeight={700}>
                                                Created:
                                            </Box>{" "}
                                            {formatDate(report.created_at)}
                                        </Typography>

                                        <Typography variant="body2">
                                            <Box component="span" fontWeight={700}>
                                                Completed:
                                            </Box>{" "}
                                            {formatDate(report.completed_at)}
                                        </Typography>
                                    </Stack>
                                </Section>

                                {!!report.raw_source_summary?.length && (
                                    <Section title="Raw source summary" icon={<PublicIcon />}>
                                        <Stack spacing={1}>
                                            {report.raw_source_summary.map((item: string, index: number) => (
                                                <Typography key={index} variant="body2" sx={{ lineHeight: 1.7 }}>
                                                    • {item}
                                                </Typography>
                                            ))}
                                        </Stack>
                                    </Section>
                                )}

                                {!!report.limitations?.length && (
                                    <Section title="Limitations" icon={<FactCheckIcon />}>
                                        <Stack spacing={1}>
                                            {report.limitations.map((item: string, index: number) => (
                                                <Typography key={index} variant="body2" sx={{ lineHeight: 1.7 }}>
                                                    • {item}
                                                </Typography>
                                            ))}
                                        </Stack>
                                    </Section>
                                )}
                            </Stack>
                        </Box>
                    )}
                </Box>
            </CardContent>
        </Card>
    )
}

function Section({
    title,
    icon,
    children,
}: {
    title: string
    icon?: React.ReactNode
    children: React.ReactNode
}) {
    return (
        <Box>
            <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                {icon}

                <Typography variant="subtitle1" fontWeight={700}>
                    {title}
                </Typography>
            </Stack>

            {children}
        </Box>
    )
}

const formatUtcDate = (timestamp?: number) => {
    if (!timestamp) return "—"

    const date = new Date(timestamp * 1000)

    const pad = (n: number) => n.toString().padStart(2, "0")

    return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

function InfoChip({
    icon,
    label,
    value,
}: {
    icon?: React.ReactElement
    label: string
    value?: any
}) {
    return (
        <Chip
            icon={icon}
            label={
                <Box component="span">
                    <Box component="span" fontWeight={700}>
                        {label}:
                    </Box>{" "}
                    {value ?? "—"}
                </Box>
            }
            variant="filled"
            sx={{
                color: "#000", 
                height: "auto",
                py: 0.75,
                "& .MuiChip-label": {
                    whiteSpace: "normal",
                },
            }}
        />
    )
}